import { useState, useEffect, useCallback } from "react";
import { authenticatedFetch } from "../utils/api";
import { API_HOST } from "../utils/constants";
import { useUser } from "../context/UserContext";

const EMPTY_PERMISSIONS = new Set();
const PROJECT_ROLES = new Set(["owner", "editor"]);

const useProjectPermissions = (projectId) => {
  const user = useUser();
  const scope = JSON.stringify([String(projectId || ""), user?.userId, user?.token, user?.role]);
  const [result, setResult] = useState(null);
  const [revision, setRevision] = useState(0);
  // Scope the result during render, not only in the effect: a previous project or
  // login session must never grant access for even the first render of the next one.
  const current = result?.scope === scope ? result : null;
  const loading = Boolean(projectId) && !current;
  const role = current?.role || "";
  const permissions = current?.permissions || EMPTY_PERMISSIONS;

  useEffect(() => {
    // Do not retain A as a reusable grant while B is pending, or while leaving
    // project routes altogether; returning to A must verify access again.
    setResult(null);
    if (!projectId) return undefined;

    const controller = new AbortController();
    const fetchPermissions = async () => {
      try {
        const data = await authenticatedFetch(
          `${API_HOST}/api/project/member/permissions?projectId=${encodeURIComponent(projectId)}`,
          { signal: controller.signal }
        );
        if (!controller.signal.aborted) {
          const verifiedRole = PROJECT_ROLES.has(data?.role) ? data.role : "";
          setResult({ scope, role: verifiedRole, permissions: verifiedRole && Array.isArray(data?.permissions) ? new Set(data.permissions) : EMPTY_PERMISSIONS, error: false });
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          const denied = Number(error?.status || error?.code) === 403;
          setResult({ scope, role: "", permissions: EMPTY_PERMISSIONS, error: !denied });
        }
      }
    };

    fetchPermissions();
    return () => controller.abort();
  }, [projectId, scope, revision]);

  const can = useCallback((perm) => permissions.has(perm), [permissions]);
  const retry = useCallback(() => { setResult(null); setRevision(value => value + 1); }, []);

  return { permissions, role, loading, can, error: current?.error || false, retry };
};

export default useProjectPermissions;
